import React, { useEffect, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, Animated, Dimensions } from 'react-native';
import { useTheme } from '../../theme/ThemeContext';

export interface SegmentOption<T> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T> {
  segments: SegmentOption<T>[];
  selectedValue: T;
  onValueChange: (value: T) => void;
  backgroundColor?: string;
  activeColor?: string;
  activeTextColor?: string;
  inactiveTextColor?: string;
}

const { width } = Dimensions.get('window');

function SegmentedControl<T extends string>({ 
  segments,
  selectedValue,
  onValueChange,
  backgroundColor,
  activeColor,
  activeTextColor = '#000000',
  inactiveTextColor = '#000000',
}: SegmentedControlProps<T>) {
  const { theme, isDark } = useTheme();
  const slideAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;
  
  const SEGMENT_WIDTH = (width - 40) / segments.length; // 40 = marginHorizontal(16) * 2 + padding(4) * 2
  
  const getIndexForValue = (value: T): number => {
    return segments.findIndex(seg => seg.value === value);
  };

  useEffect(() => {
    const index = getIndexForValue(selectedValue);
    
    // Animate indicator slide
    Animated.spring(slideAnim, {
      toValue: index * SEGMENT_WIDTH,
      useNativeDriver: true,
      tension: 80,
      friction: 10,
    }).start();

    // Pulse animation for feedback
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 0.95,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();
  }, [selectedValue, SEGMENT_WIDTH]);
  
  const containerBg = backgroundColor ?? (isDark ? '#4C4740' : '#ffffff');

  return (
    <View style={[styles.container, { backgroundColor: containerBg }]}>
      {/* Animated Indicator */}
      <Animated.View
        style={[
          styles.indicator,
          {
            backgroundColor: activeColor || theme.primary,
            transform: [
              { translateX: slideAnim },
              { scale: scaleAnim }
            ],
            width: SEGMENT_WIDTH,
          },
        ]}
      />
      
      {/* Segments */}
      {segments.map((segment) => (
        <TouchableOpacity
          key={segment.value}
          style={styles.segment}
          onPress={() => onValueChange(segment.value)}
          activeOpacity={0.7}
        >
          <Animated.Text 
            style={[
              styles.segmentText, 
              {
                color: selectedValue === segment.value
                  ? activeTextColor
                  : inactiveTextColor,
                fontWeight: selectedValue === segment.value ? '700' : '600',
              }
            ]}
          >
            {segment.label}
          </Animated.Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    borderRadius: 999,
    padding: 4,
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 12,
    position: 'relative',
    height: 44,
  },
  indicator: {
    position: 'absolute',
    left: 4,
    top: 4,
    bottom: 4,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 3,
    zIndex: 1,
  },
  segment: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  segmentText: {
    fontSize: 14,
    zIndex: 3,
  },
});

export default SegmentedControl;
